import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import EditablePageIntro from '../components/EditablePageIntro';
import DisplayPrice from '../components/DisplayPrice';
import { Check, Loader2 } from 'lucide-react';
import useMembershipTiers from '../hooks/useMembershipTiers';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MembershipPage() {
    const { user, isAuthenticated } = useAuth();
    const [searchParams] = useSearchParams();
    const preselectedTier = searchParams.get('tier');
    const { tiers: allTiers } = useMembershipTiers();
    const purchasableTiers = allTiers.filter(t => !t.is_invite_only);

    const [selectedTier, setSelectedTier] = useState(preselectedTier || '');
    const [billingPeriod, setBillingPeriod] = useState('monthly');
    const [loading, setLoading] = useState(false);
    // Guest checkout modal state — a non-logged-in visitor clicking "Join" opens
    // this dialog so we can collect email + name, then start Stripe checkout
    // WITHOUT creating a profile first. The account is minted only after payment
    // completes (see backend `_fulfill_membership`).
    const [guestModal, setGuestModal] = useState({ open: false, tier: null });
    const [guestName, setGuestName] = useState('');
    const [guestEmail, setGuestEmail] = useState('');
    const [guestEmailConflict, setGuestEmailConflict] = useState(null);

    const startCheckout = async (tierId, { email, name } = {}) => {
        setLoading(true);
        setSelectedTier(tierId);
        try {
            const { data } = await axios.post(
                `${API_URL}/api/membership/checkout`,
                {
                    tier: tierId,
                    billing_period: billingPeriod,
                    origin_url: window.location.origin,
                    email,
                    name,
                },
                { withCredentials: true }
            );
            window.location.href = data.url;
        } catch (error) {
            const detail = error.response?.data?.detail;
            if (error.response?.status === 409 && typeof detail === 'object' && detail?.code === 'account_exists') {
                setGuestEmailConflict({ name: detail.name || '' });
                toast.error(detail.message || 'Please sign in first.');
            } else {
                toast.error((typeof detail === 'string' ? detail : detail?.message) || 'Failed to start checkout. Please try again.');
            }
            setLoading(false);
            setSelectedTier('');
        }
    };

    const handleCheckout = (tierId) => {
        if (!isAuthenticated) {
            setGuestModal({ open: true, tier: tierId });
            setGuestName('');
            setGuestEmail('');
            setGuestEmailConflict(null);
            return;
        }
        startCheckout(tierId);
    };

    const checkGuestEmail = async () => {
        const e = (guestEmail || '').trim().toLowerCase();
        if (!e) return;
        try {
            const { data } = await axios.get(`${API_URL}/api/auth/check-email`, { params: { email: e } });
            setGuestEmailConflict(data.exists ? { name: data.name } : null);
        } catch (_err) {
            setGuestEmailConflict(null);
        }
    };

    const submitGuestCheckout = (e) => {
        e.preventDefault();
        if (!guestEmail.trim()) return toast.error('Please enter your email address');
        if (guestEmailConflict) return toast.error('An account already exists — please sign in first.');
        startCheckout(guestModal.tier, { email: guestEmail.trim().toLowerCase(), name: guestName.trim() });
    };

    return (
        <div className="min-h-screen bg-[#F9F6F0]">
            <Navbar />

            <main className="pt-24 pb-16" data-testid="membership-page">
                <div className="max-w-7xl mx-auto px-6 sm:px-12">
                    {/* Header */}
                    <div className="text-center mb-16">
                        <p className="overline mb-4">Mirror Circle</p>
                        <h1 className="heading-2 mb-6">Choose Your Path</h1>
                        <EditablePageIntro
                            pageKey="membership"
                            className="max-w-2xl mx-auto"
                            fallback={<p className="body-text">Membership is not a gate — it is a choice to receive more, for less, and to be held inside a living community.</p>}
                        />
                    </div>

                    {/* Billing Toggle */}
                    <div className="flex justify-center mb-12">
                        <div className="inline-flex bg-white border border-[#E5E0D8] rounded-sm p-1" data-testid="billing-toggle">
                            <button
                                onClick={() => setBillingPeriod('monthly')}
                                className={`px-6 py-2 rounded-sm text-sm font-sans transition-all ${
                                    billingPeriod === 'monthly' 
                                        ? 'bg-[#5B2D8E] text-white' 
                                        : 'text-[#4A4A68] hover:text-[#5B2D8E]'
                                }`}
                                data-testid="billing-monthly"
                            >
                                Monthly
                            </button>
                            <button
                                onClick={() => setBillingPeriod('yearly')}
                                className={`px-6 py-2 rounded-sm text-sm font-sans transition-all ${
                                    billingPeriod === 'yearly' 
                                        ? 'bg-[#5B2D8E] text-white' 
                                        : 'text-[#4A4A68] hover:text-[#5B2D8E]'
                                }`}
                                data-testid="billing-yearly"
                            >
                                Yearly (Save 1 month)
                            </button>
                        </div>
                    </div>

                    {/* Current Membership Notice */}
                    {user?.membership_tier && (
                        <div className="mb-8 p-4 bg-[#D8B4E2]/20 border border-[#D8B4E2] rounded-sm text-center" data-testid="current-membership-notice">
                            <p className="font-sans text-[#5B2D8E]">
                                You are currently a <span className="font-medium uppercase">{user.membership_tier}</span> member.
                                {user.membership_expires && (
                                    <span> Your membership expires on {new Date(user.membership_expires).toLocaleDateString()}.</span>
                                )}
                            </p>
                        </div>
                    )}

                    {/* Membership Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
                        {purchasableTiers.map((tier, index) => {
                            const highlighted = purchasableTiers.length > 1 && index === Math.floor(purchasableTiers.length / 2);
                            const price = billingPeriod === 'yearly' && tier.price_yearly != null ? tier.price_yearly : tier.price_monthly;
                            const isCurrentTier = user?.membership_tier === tier.key;

                            return (
                                <div
                                    key={tier.key}
                                    className={`membership-card ${
                                        highlighted
                                            ? 'p-10 md:p-14 bg-[#5B2D8E] text-[#F9F6F0] shadow-xl rounded-sm flex flex-col'
                                            : 'p-10 md:p-14 bg-white border border-[#E5E0D8] shadow-sm rounded-sm flex flex-col'
                                    }`}
                                    data-testid={`membership-card-${tier.key}`}
                                >
                                    <div className="mb-8">
                                        <p className={`text-xs uppercase tracking-[0.2em] font-medium mb-2 ${highlighted ? 'text-[#D4AF37]' : 'text-[#5B2D8E]'}`}>
                                            {tier.name}
                                        </p>
                                        {tier.description && (
                                            <p className={`font-serif text-xl mb-4 ${highlighted ? 'text-[#F9F6F0]' : 'text-[#1A1A2E]'}`}>
                                                {tier.description}
                                            </p>
                                        )}
                                        <div className="flex items-baseline gap-1">
                                            <DisplayPrice
                                                amount={price}
                                                currency="eur"
                                                className={`font-serif text-5xl font-light ${highlighted ? 'text-[#F9F6F0]' : 'text-[#1A1A2E]'}`}
                                                noteClassName={highlighted ? 'text-[#D8B4E2]' : 'text-[#4A4A68]'}
                                            />
                                            <span className={`font-sans text-sm ${highlighted ? 'text-[#F9F6F0]/80' : 'text-[#4A4A68]'}`}>
                                                /{billingPeriod === 'yearly' ? 'year' : 'month'}
                                            </span>
                                        </div>
                                        {tier.min_months > 1 && (
                                            <p className={`font-sans text-xs mt-2 italic ${highlighted ? 'text-[#D8B4E2]' : 'text-[#4A4A68]'}`}>
                                                Minimum {tier.min_months} months
                                            </p>
                                        )}
                                    </div>

                                    <ul className="space-y-4 mb-8 flex-grow">
                                        {(tier.features || []).map((feature, i) => (
                                            <li key={i} className="flex items-start gap-3">
                                                <Check className={`w-5 h-5 flex-shrink-0 mt-0.5 ${highlighted ? 'text-[#D4AF37]' : 'text-[#5B2D8E]'}`} />
                                                <span className={`font-sans text-sm ${highlighted ? 'text-[#F9F6F0]/90' : 'text-[#4A4A68]'}`}>
                                                    {feature}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>

                                    <button
                                        onClick={() => handleCheckout(tier.key)}
                                        disabled={loading || isCurrentTier}
                                        className={`w-full text-center py-4 rounded-sm font-sans text-sm uppercase tracking-widest transition-all flex items-center justify-center gap-2 ${
                                            isCurrentTier
                                                ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                                : highlighted
                                                    ? 'bg-[#F9F6F0] text-[#5B2D8E] hover:bg-[#D4AF37] hover:text-[#1A1A2E]'
                                                    : 'bg-[#5B2D8E] text-[#F9F6F0] hover:bg-[#4A2475]'
                                        }`}
                                        data-testid={`checkout-${tier.key}-btn`}
                                    >
                                        {loading && selectedTier === tier.key ? (
                                            <>
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                                Processing...
                                            </>
                                        ) : isCurrentTier ? (
                                            'Current Plan'
                                        ) : (
                                            `Choose ${tier.name}`
                                        )}
                                    </button>
                                </div>
                            );
                        })}
                    </div>

                    {/* Partnership Section */}
                    <div className="mt-16 p-12 bg-[#F2EBE5] rounded-sm text-center" data-testid="partnership-section">
                        <p className="overline mb-4">Mirror Circle Partnership</p>
                        <h3 className="heading-3 mb-4">For the Pillars of the School</h3>
                        <p className="body-text mb-6 max-w-2xl mx-auto">
                            Trainers, speakers, event hosts, contributors — those who run the show from inside.
                            Interested in becoming a Pillar?
                        </p>
                        <Link to="/#partnership" className="btn-secondary">
                            Get in Touch
                        </Link>
                    </div>
                </div>
            </main>

            {/* Guest checkout modal — collects email + name so we can start Stripe.
                The user account itself is created ONLY after payment succeeds. */}
            {guestModal.open && (
                <div className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-4" onClick={() => setGuestModal({ open: false, tier: null })} data-testid="guest-checkout-modal">
                    <div className="bg-white rounded-sm max-w-md w-full p-6 sm:p-8" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <p className="overline">Join the circle</p>
                                <h3 className="heading-3">Almost there</h3>
                            </div>
                            <button
                                onClick={() => setGuestModal({ open: false, tier: null })}
                                className="text-[#4A4A68] hover:text-[#5B2D8E]"
                                aria-label="Close"
                            >
                                ×
                            </button>
                        </div>
                        <p className="text-sm text-[#4A4A68] mb-6">
                            We'll take payment first, then create your profile automatically. Your account will be ready as soon as Stripe confirms — no separate sign-up.
                        </p>
                        <form onSubmit={submitGuestCheckout} className="space-y-4">
                            <div>
                                <label className="block font-sans text-sm text-[#1A1A2E] mb-2">Name</label>
                                <input
                                    type="text"
                                    value={guestName}
                                    onChange={(e) => setGuestName(e.target.value)}
                                    className="w-full"
                                    placeholder="Your name"
                                    data-testid="membership-guest-name"
                                />
                            </div>
                            <div>
                                <label className="block font-sans text-sm text-[#1A1A2E] mb-2">Email *</label>
                                <input
                                    type="email"
                                    required
                                    value={guestEmail}
                                    onChange={(e) => { setGuestEmail(e.target.value); setGuestEmailConflict(null); }}
                                    onBlur={checkGuestEmail}
                                    className="w-full"
                                    placeholder="you@example.com"
                                    data-testid="membership-guest-email"
                                />
                                {guestEmailConflict && (
                                    <div className="mt-2 p-3 bg-[#5B2D8E]/10 border border-[#5B2D8E]/30 rounded-sm text-sm text-[#1A1A2E]" data-testid="membership-email-conflict">
                                        <strong>An account with this email already exists.</strong>{' '}
                                        <Link to={`/login?next=/membership`} className="text-[#5B2D8E] hover:underline">Sign in</Link>{' '}
                                        or{' '}
                                        <Link to="/forgot-password" className="text-[#5B2D8E] hover:underline">reset your password</Link>.
                                    </div>
                                )}
                            </div>
                            <div className="flex items-center justify-between text-sm text-[#4A4A68]">
                                <span>Already a member?</span>
                                <Link to="/login?next=/membership" className="text-[#5B2D8E] hover:underline" data-testid="membership-signin-link">
                                    Sign in →
                                </Link>
                            </div>
                            <button
                                type="submit"
                                disabled={loading}
                                className="btn-primary w-full flex items-center justify-center gap-2"
                                data-testid="membership-guest-checkout"
                            >
                                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Continue to payment'}
                            </button>
                        </form>
                    </div>
                </div>
            )}

            <Footer />
        </div>
    );
}
